import { LoadingOverlay, Stack } from '@mantine/core';
import { useGetEntityLineageGraph } from '@repo/api-codegen';
import { useInViewport } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { MiniMap, ReactFlowProvider } from 'reactflow';
import {
	FilterOptionType,
	SearchFilterV2StoreContext,
} from '../../../components/Filter';
import BottomPanel from '../../../components/LineageGraph/BottomPanel';
import { Controls } from '../../../components/LineageGraph/Controls';
import { ChildEdge, EntityEdge } from '../../../components/LineageGraph/Edges';
import EntityNode from '../../../components/LineageGraph/EntityNode';
import { FilterBar } from '../../../components/LineageGraph/FilterBar';
import { useStyles } from '../../../components/LineageGraph/LineageGraph.styles';
import LineageGraphContainer from '../../../components/LineageGraph/LineageGraphContainer';
import {
	CreationQueryModal,
	ImpactAnalysisModal,
	TestsModal,
} from '../../../components/LineageGraph/Modals';
import { lineageStore } from '../../../components/LineageGraph/store';
import TemporaryNode from '../../../components/LineageGraph/TemporaryNode';
import type {
	EntityNodeData,
	LineageGraphData,
} from '../../../components/LineageGraph/types';
import {
	EdgeType,
	LineageGraphModalType,
	NodeType,
} from '../../../components/LineageGraph/types';

import type { FlowActions } from '@repo/lineage/store/LineageFlowUIStore.types';
import { FILTER_OPTIONS_CONFIG } from '../../../components/Filter/constants';
import { Legend } from '../../../components/LineageGraph/Legend/Legend';

const nodeTypes = {
	[NodeType.ENTITY as string]: EntityNode,
	[NodeType.TEMPORARY as string]: TemporaryNode,
};

const edgeTypes = {
	[EdgeType.ENTITY]: EntityEdge,
	[EdgeType.CHILD]: ChildEdge,
};

interface InspectLineageGraphProps {
	id: string;
	includeArchived?: boolean;
	onError?: (error: string) => void;
	flowActions?: FlowActions;
	// Custom menu actions for entity nodes
	customMenuActions?: Array<{
		id: string;
		icon: string;
		label: string;
		condition?: (nodeData: EntityNodeData) => boolean;
		onClick: (nodeData: EntityNodeData) => void;
	}>;
	// Custom icon buttons for entity nodes
	customIconButtons?: Array<{
		id: string;
		icon: string;
		tooltip: string;
		condition?: (nodeData: EntityNodeData) => boolean;
		onClick: (nodeData: EntityNodeData) => void;
	}>;
}

const InspectLineageGraph = observer(
	({
		id,
		includeArchived = false,
		customMenuActions,
		customIconButtons,
	}: InspectLineageGraphProps) => {
		const { data } = useGetEntityLineageGraph<LineageGraphData>({
			queryParams: {
				entity_id: id,
				include_archived: includeArchived,
			},
		});

		const entity = data?.entity;

		const viewportRef = useRef<HTMLDivElement>(null);
		const [inViewport] = useInViewport(viewportRef);
		const [viewInitialized, setViewInitialized] = useState(false);

		const filterBarStore = useContext(SearchFilterV2StoreContext);
		filterBarStore.filterOptions = [
			FILTER_OPTIONS_CONFIG[FilterOptionType.NATIVE_TYPE],
			FILTER_OPTIONS_CONFIG[FilterOptionType.INTEGRATION],
		];

		const { classes } = useStyles();

		// Reset the lineage store when the component is unmounted
		useEffect(() => () => lineageStore.reset({}), []);

		// Initialize lineage data
		useEffect(() => {
			if (data) {
				lineageStore.reset({ resetReactFlowInstance: false });
				lineageStore.initialize(data, filterBarStore.catalogFilter);
			}
		}, [data, filterBarStore.catalogFilter]);

		// Navigate to the root node when in the viewport
		useEffect(() => {
			if (inViewport && !viewInitialized && entity?.id) {
				lineageStore.navigateToNode(entity.id, 0.5, 0);
				setViewInitialized(true);
			}
		}, [entity?.id, inViewport, viewInitialized]);

		const isLoading =
			lineageStore.isLoading || !viewInitialized || !lineageStore.nodes.length;

		// Create custom node types with custom actions
		const finalNodeTypes = useMemo(() => {
			if (!customMenuActions && !customIconButtons) {
				return nodeTypes;
			}

			// Create a custom EntityNode component that includes custom actions
			function CustomEntityNode(props: any) {
				return (
					<EntityNode
						{...props}
						customMenuActions={customMenuActions}
						customIconButtons={customIconButtons}
					/>
				);
			}

			return {
				...nodeTypes,
				[NodeType.ENTITY as string]: CustomEntityNode,
			};
		}, [customMenuActions, customIconButtons]);

		return (
			<SearchFilterV2StoreContext.Provider value={filterBarStore}>
				<ReactFlowProvider>
					<LineageGraphContainer>
						<Stack ref={viewportRef} className={classes.wrapper}>
							<FilterBar
								toggleFullscreen={lineageStore.toggleIsFullscreen}
								entity={entity}
							/>
							<LoadingOverlay
								data-testid={'lineage-loading-overlay'}
								visible={isLoading}
							/>
							<ImpactAnalysisModal />
							<ReactFlow
								className={classes.reactFlow}
								onInit={lineageStore.setReactFlowInstance}
								nodeTypes={finalNodeTypes}
								edgeTypes={edgeTypes}
								nodes={lineageStore.nodes}
								edges={lineageStore.computedEdges}
								onNodesChange={lineageStore.onNodesChange}
								onEdgesChange={lineageStore.onEdgesChange}
								onEdgeUpdate={lineageStore.onEdgeUpdate}
								onConnect={lineageStore.onConnect}
								onConnectStart={lineageStore.onConnectStart}
								onConnectEnd={lineageStore.onConnectEnd}
								onEdgeMouseEnter={lineageStore.onEdgeMouseEnter}
								onEdgeMouseLeave={lineageStore.onEdgeMouseLeave}
								nodesFocusable={false}
								edgesFocusable={false}
								nodesDraggable={false}
								nodesConnectable
								elementsSelectable
								snapToGrid={false}
								minZoom={0.1}
								onlyRenderVisibleElements
								fitView
							>
								<Controls />
								<Legend />
								{lineageStore.isFullscreen && (
									<MiniMap pannable zoomable position="top-right" />
								)}
								<BottomPanel />
							</ReactFlow>
							<TestsModal
								opened={lineageStore.modals[LineageGraphModalType.TESTS].opened}
								onClose={() => lineageStore.setTestsModalOpen(false)}
								tests={lineageStore.modals[LineageGraphModalType.TESTS].tests}
								monitors={
									lineageStore.modals[LineageGraphModalType.TESTS].monitors
								}
							/>
							<CreationQueryModal
								opened={
									lineageStore.modals[LineageGraphModalType.CREATION_QUERY]
										.opened
								}
								onClose={() => lineageStore.setCreationQueryModalOpen(false)}
								query={
									lineageStore.modals[LineageGraphModalType.CREATION_QUERY]
										.query
								}
							/>
						</Stack>
					</LineageGraphContainer>
				</ReactFlowProvider>
			</SearchFilterV2StoreContext.Provider>
		);
	}
);

export { InspectLineageGraph };
